import { Text } from '@amzn/meridian';
import { isEmpty } from 'es-toolkit/compat';
import { type FC, useMemo } from 'react';
import Select from 'react-select';
import CreatableSelect, { type CreatableProps } from 'react-select/creatable';
import { classes } from 'typestyle';

export type TagOption = {
  label: string;
  value: string;
};

export type GroupOption = {
  options: TagOption[];
};

export interface TagSelectProps extends CreatableProps<TagOption, true, GroupOption> {
  inputId: string;
  invalid: boolean;
  errors?: string[];
  description?: string | (() => JSX.Element);
  disabled?: boolean;
  isCreatable?: boolean;
  placeholder?: string;
}

export const TagSelect: FC<TagSelectProps> = ({
  inputId,
  invalid,
  errors,
  description,
  disabled,
  placeholder,
  isCreatable = true,
  ...props
}) => {
  const styles = useMemo(
    () => ({
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      control: (base: any) => ({
        ...base,
        borderColor: invalid ? '#dc3545' : base.borderColor,
      }),
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      menu: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
    }),
    [
      invalid,
    ],
  );

  return (
    <>
      {isCreatable ? (
        <CreatableSelect
          className={classes({
            'is-invalid': invalid,
          })}
          inputId={inputId}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          placeholder={placeholder ?? 'Enter tags...'}
          styles={styles}
          {...props}
        />
      ) : (
        <Select
          className={classes({
            'is-invalid': invalid,
          })}
          inputId={inputId}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          placeholder={placeholder}
          styles={styles}
          {...props}
        />
      )}

      {!isEmpty(errors) &&
        errors?.map((err) => (
          <Text color="error" key={err} tag="div" type="b200">
            {err}
          </Text>
        ))}
      {description && (
        <Text color="secondary" id={`${inputId}-description`}>
          {typeof description === 'string' ? description : description()}
        </Text>
      )}
    </>
  );
};
